import React from "react";
import { Box, Button, Card, IconButton, Typography } from "@material-ui/core";
import clsx from "clsx";
import moment from "moment";
import EventRepeatIcon from "@material-ui/icons/CachedRounded";
import ChevronLeftRoundedIcon from "@material-ui/icons/ChevronLeftRounded";
import ChevronRightRoundedIcon from "@material-ui/icons/ChevronRightRounded";
import {
  FOLLOWUP_STATUS_ORDER,
  dayKey,
  groupByDay,
  needsAction,
  sortByStatus,
  statusMeta,
} from "../../followUpHelpers";
import { useCalendarStyles } from "./styles";

const WEEKDAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const MAX_DOTS = 3;

/**
 * Month grid of follow-ups. Follow-ups carry a date but no time, so a month
 * view (rather than the hourly grid the Appointments page uses) is what fits:
 * each day shows one status-coloured dot per follow-up, and a past day that
 * still holds a Pending/Rescheduled follow-up is outlined as overdue.
 */
const FollowUpCalendar = ({
  followUps = [],
  calMonth,
  selectedDate,
  onSelectDay,
  onPrevMonth,
  onNextMonth,
  onToday,
}) => {
  const classes = useCalendarStyles();

  const monthStart = calMonth.clone().startOf("month");
  const gridStart = monthStart.clone().startOf("isoWeek");
  // Only render the weeks the month actually touches (5 or 6), not a fixed 42.
  const weeks = Math.ceil(
    (monthStart.diff(gridStart, "days") + monthStart.daysInMonth()) / 7
  );
  const days = Array.from({ length: weeks * 7 }, (_, i) =>
    gridStart.clone().add(i, "days")
  );

  const byDay = groupByDay(followUps);
  const today = moment().startOf("day");

  return (
    <Card className={classes.calendarCard} elevation={0}>
      <Box className={classes.cardHeader}>
        <Box className={classes.headerTitleWrap}>
          <EventRepeatIcon className={classes.headerIcon} />
          <Typography className={classes.headerTitle}>
            {calMonth.format("MMMM YYYY")}
          </Typography>
        </Box>

        <Box className={classes.headerControls}>
          <Box className={classes.navGroup}>
            <IconButton
              className={classes.navButton}
              size="small"
              onClick={onPrevMonth}
              aria-label="previous month"
            >
              <ChevronLeftRoundedIcon />
            </IconButton>
            <IconButton
              className={classes.navButton}
              size="small"
              onClick={onNextMonth}
              aria-label="next month"
            >
              <ChevronRightRoundedIcon />
            </IconButton>
          </Box>
          <Button
            disableElevation
            className={classes.todayBtn}
            onClick={onToday}
          >
            Today
          </Button>
        </Box>
      </Box>

      <Box className={classes.monthGrid}>
        {WEEKDAY_LABELS.map((label) => (
          <Box key={label} className={classes.weekdayCell}>
            {label}
          </Box>
        ))}

        {days.map((day) => {
          const key = dayKey(day);
          const dayFollowUps = sortByStatus(byDay[key] || []);
          const isOutside = day.month() !== monthStart.month();
          const isToday = day.isSame(today, "day");
          const isSelected = selectedDate && day.isSame(selectedDate, "day");
          const isOverdue =
            day.isBefore(today, "day") && dayFollowUps.some(needsAction);
          const overflow = dayFollowUps.length - MAX_DOTS;

          return (
            <Box
              key={key}
              className={clsx(
                classes.dayCell,
                isOutside && classes.dayCellOutside,
                isOverdue && classes.dayCellOverdue,
                isSelected && classes.dayCellSelected
              )}
              onClick={() => onSelectDay(day)}
            >
              <Typography
                component="span"
                className={clsx(
                  classes.dayNumber,
                  isToday && classes.dayNumberToday
                )}
              >
                {day.format("D")}
              </Typography>

              {dayFollowUps.length > 0 && (
                <>
                  <Box className={classes.dotRow}>
                    {dayFollowUps.slice(0, MAX_DOTS).map((fu) => (
                      <Box
                        key={fu.id}
                        className={classes.dot}
                        style={{ backgroundColor: statusMeta(fu.status).dot }}
                      />
                    ))}
                    {overflow > 0 && (
                      <Typography
                        component="span"
                        className={classes.overflowCount}
                      >
                        +{overflow}
                      </Typography>
                    )}
                  </Box>
                  <Typography
                    component="span"
                    className={clsx(
                      classes.countLabel,
                      isOverdue && classes.overdueLabel
                    )}
                  >
                    {dayFollowUps.length}
                    {dayFollowUps.length === 1 ? " follow-up" : " follow-ups"}
                    {isOverdue ? " · overdue" : ""}
                  </Typography>
                </>
              )}
            </Box>
          );
        })}
      </Box>

      <Box className={classes.legend}>
        {FOLLOWUP_STATUS_ORDER.map((status) => {
          const meta = statusMeta(status);
          return (
            <Box key={status} className={classes.legendItem}>
              <Box
                className={classes.dot}
                style={{ backgroundColor: meta.dot }}
              />
              <Typography className={classes.legendLabel}>
                {meta.label}
              </Typography>
            </Box>
          );
        })}
      </Box>
    </Card>
  );
};

export default FollowUpCalendar;
